import React from 'react';
import { CheckCircle } from 'lucide-react';
import { BETA_ANNUAL_DISCOUNT_PERCENT, formatCurrency, getPlanConfig, normalizePlanId } from '../config/pricing';

interface PricingStepCardProps {
  tier: 'starter' | 'growth' | 'pro_fleet' | 'concierge' | 'pro' | 'managed';
  highlighted?: boolean;
  onSelect: () => void;
}

export const PricingStepCard: React.FC<PricingStepCardProps> = ({
  tier,
  highlighted = false,
  onSelect
}) => {
  const plan = getPlanConfig(tier);
  const normalizedTier = normalizePlanId(tier);
  const features: Record<typeof normalizedTier, string[]> = {
    starter: ['Unlimited PCNs', 'All five AI assistants', 'Per-PCN AI summary'],
    growth: ['Unlimited PCNs', '3 team seats', 'Monthly aggregate report', 'Recovery reporting'],
    pro_fleet: ['Unlimited PCNs', '10 team seats', 'Evidence packs', 'Audit trail'],
    concierge: ['Unlimited team seats', 'Managed onboarding', 'Dedicated account contact', 'Custom SLA'],
  };

  return (
    <div className={`
      flex h-full min-h-[480px] flex-col rounded-lg border p-7 transition-all
      ${highlighted
        ? 'border-primary-500 ring-2 ring-primary-500 shadow-lg shadow-primary-500/20'
        : 'border-slate-200 hover:border-slate-300 dark:border-slate-700 dark:hover:border-slate-600'
      }
      bg-white dark:bg-slate-800
    `}>
      {highlighted && (
        <div className="absolute -mt-10 rounded-full bg-primary-600 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white shadow-md">
          Recommended
        </div>
      )}

      <h3 className="text-xl font-bold text-slate-900 dark:text-white">{plan.shortName}</h3>
      <p className="mt-2 min-h-[48px] text-sm leading-6 text-slate-600 dark:text-slate-300">{plan.description}</p>

      <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/40">
        <div className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          FleetMode plan
        </div>
        <div className="mt-1 text-3xl font-bold text-slate-900 dark:text-white">
          {plan.monthlyFee === null ? 'Custom' : `${formatCurrency(plan.monthlyFee)}/mo`}
        </div>
        <div className="mt-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
          {plan.seats}
        </div>
        <div className="mt-3 text-[11px] leading-5 text-slate-500 dark:text-slate-400">
          Unlimited PCNs included.
        </div>
        {plan.yearlyFee !== null && (
          <div className="mt-2 text-[11px] font-bold uppercase tracking-wide text-primary-700 dark:text-primary-300">
            Yearly beta lock-in: {BETA_ANNUAL_DISCOUNT_PERCENT}% off first year
          </div>
        )}
      </div>

      <ul className="mt-5 flex-1 space-y-2">
        {features[normalizedTier].map((feature) => (
          <li key={feature} className="flex items-start gap-2">
            <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-500 dark:text-primary-400" />
            <span className="text-sm text-slate-700 dark:text-slate-300">{feature}</span>
          </li>
        ))}
      </ul>

      <button
        onClick={onSelect}
        className={`mt-5 w-full rounded-lg py-3 font-semibold transition-all ${
          highlighted
            ? 'bg-primary-600 text-white shadow-md hover:bg-primary-700 hover:shadow-lg hover:shadow-primary-500/30'
            : 'bg-slate-100 text-slate-900 hover:bg-slate-200 dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600'
        }`}
      >
        {normalizedTier === 'concierge' ? 'Book plan review' : 'Choose plan'}
      </button>
    </div>
  );
};
